import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Tab, Form } from "semantic-ui-react";
import { TimeInput } from "semantic-ui-calendar-react";
import BlurChangeInput from "../controls/blurChangeInput";
import { SingleDatePicker } from "react-dates";
import MetacomSiteSelector from "../../addons/MetacomSiteSelector";


export const useTimeHighlight = (busyRanges) => {
	const activeFieldRef = useRef(null);

	useEffect(() => {

		const toMinutes = (time) => {
    	  if (!time || !time.includes(":")) return -1;
    	  const [h, m] = time.split(":").map(Number);
    	  return h * 60 + m;
    	};

    	const isBusyTime = (time, ranges = [], step = 60) => {
    	  const tStart = toMinutes(time);
    	  const tEnd = tStart + step;

    	  return ranges.some(({ from, to }) => {
    	    const rStart = toMinutes(from);
    	    const rEnd = toMinutes(to);

    	    return tStart <= rEnd && tEnd > rStart;
    	  });
    	};

    	const getStep = (items) => {
    	  if (items.length < 2) return 60;

    	  const t1 = toMinutes(items[0].innerText.trim());
    	  const t2 = toMinutes(items[1].innerText.trim());

    	  return t2 - t1 || 60;
    	};

		const observer = new MutationObserver(() => {
		    const popup = document.querySelector(".ui.popup.visible");
		    if (!popup) return;	

		    const items = popup.querySelectorAll(".suicr-content-item");
		    if (!items.length) return;	

			const step = getStep(items);
		    items.forEach((el) => {
		    	const timeText = el.innerText.trim();
		    	const parent = el.parentElement;	
				
				if (!parent) return;
		    	
				if (
		    	    activeFieldRef.current &&
		    	    isBusyTime(timeText, busyRanges[activeFieldRef.current] || [], step)
		    	) {
		    	    parent.style.color = "red";
		    	    parent.style.background = "rgba(255,0,0,0.1)";
		    	    parent.style.fontWeight = "bold";
		    	} else {
		    	    parent.removeAttribute("style");
		    	}
		    });
		});	

		observer.observe(document.body, {
		    childList: true,
		    subtree: true,
		});	

		return () => observer.disconnect();
	}, [busyRanges]);

	const setActiveField = (fieldName) => {
		activeFieldRef.current = fieldName;
	};	

	return { setActiveField };
};

const PROCESS_RESOURCE_MAPPING = [
    {
        process_id: "c5e73df8-f447-11ef-ada9-96000408ad3f",
        process_name: "Vorspritzen",
		resources : [ {
				resource_id: "55aeb3b2-ab6b-4def-bdae-dcef87974c6f",
				resource_name: "Unimog U1400 CHA-HR 105",
			}
		],
		groups: []
    },
    {
        process_id: "066fa31a-ffc2-11ef-ada9-96000408ad3f",
        process_name: "Rand abdichten",
		resources : [ {
				resource_id: "55aeb3b2-ab6b-4def-bdae-dcef87974c6f",
        		resource_name: "Unimog U1400 CHA-HR 105"
			}
		],
		groups: []
    },
    {
        process_id: "f1f64746-f447-11ef-ada9-96000408ad3f",
        process_name: "Fugenverguss",
		resources: [],
		groups : [ {
				group_id: "f68b5c58-174e-413c-9cf9-615256af42c4", // group id
				group_name: "Fugenverguß / Ausbesserung",
			}
		]
    },
    {
        process_id: "751b48c2-f448-11ef-ada9-96000408ad3f",
        process_name: "Ausbesserung",
		resources: [],
		groups : [ {
				group_id: "f68b5c58-174e-413c-9cf9-615256af42c4", // group id
				group_name: "Fugenverguß / Ausbesserung",
			}
		]
    },
    {
        process_id: "1f514a01-f448-11ef-ada9-96000408ad3f",
        process_name: "Kehren/Waschen",
		resources : [ {
				resource_id: "0cdeae78-73f7-4e60-869e-f66d45183cf6",
				resource_name: "Saug-/ Kehrbesen CHA-HR 219",
			}
		],
		groups: []
    },
    {
        process_id: "e14e7554-ffc1-11ef-ada9-96000408ad3f",
        process_name: "Hd-Reinigung",
		resources : [ {
				resource_id: "10b09a1e-fe03-4f13-a8bb-f7d78ef282ae",
				resource_name: "Hochdruckbesen CHA-HR 175",
			}
		],
		groups: []
    }
];

const loadProcessResources = (resources) => {
	return PROCESS_RESOURCE_MAPPING.map((process) => {
		const processResources = [...process.resources];

		process.groups.forEach((group) => {
			const resourceGroup = resources.groups.get(group.group_id);
			if (!resourceGroup) return;

			const groupResources = resourceGroup._members
				.filter((resource) => !resource._resclass?.human)
				.map((resource) => ({
					resource_id: resource.id,
					resource_name: resource.name,
				}));

			processResources.push(...groupResources);
		});

		return {
			...process,
			resources: processResources,
		};
	});
};

export const getBusyRangesForCompany = ({
  companyName, processMaster, date, deployments, processResourceMapping
}) => {
	if (companyName !== "georghuber") return null;

  	const matchedProcess = processResourceMapping.find(
  	  (m) => m.process_id === processMaster._template.id
  	);
	
	// console.log('processMaster', processResourceMapping, matchedProcess);

  	if (!matchedProcess) return null;

	const resourceIds = new Set(
		matchedProcess.resources.map((resource) => resource.resource_id)
	);

	// console.log('resourceIds', resourceIds);

	const rangeList =  [...(deployments?.values() ?? [])].filter((d) => {
    	const startDay = new Date(d._job?.start).toISOString().slice(0, 10);
    	return (
    	  startDay === date 
		  && resourceIds.has(d._resource?.id)
		//   && d._resource?.id === matchedProcess.resource_id
    	);
  	}).map((d) => ({
		from: moment(d._job.start).format("HH:mm"),
		to: moment(d._job.end).format("HH:mm"),
  	}));
	
	return rangeList.length ? rangeList : null;
};
	
const EditBasedata = (props) => {
	const { t } = useTranslation();
	const [focused, setFocused] = useState(false);

	const companyName = props.data.props.app.basedata.company?.name ?? "";
	let rangeList = null;
	let busyRanges = null; 

	if(props.data.state) {
		let [process, date] = Array.from(props.data.state.marked ?? ["#"])[0].split('#');
		let deployments = props.data.props.app.orders.deployments;
	
		const processMaster = props.data.props.app.projects.processes.get(process);

		// console.log("EditBasedata", props.data.props, process);
		if(companyName === 'georghuber') {
			const processResourceMapping = loadProcessResources(props.data.props.app.resources);
			// console.log("processResourceMapping", processResourceMapping, props.data.props.app.resources);
			rangeList = processMaster ? getBusyRangesForCompany({ companyName, processMaster, date, deployments, processResourceMapping }) : null;
			console.log("rangeList", rangeList);
			busyRanges = (rangeList) ? { start: rangeList, end: rangeList } : null;
		}
	}

	const { setActiveField } = useTimeHighlight(busyRanges);
	
	return (
		<Tab.Pane>
			<Form>
				{props.global ? (
					<React.Fragment>
						{props.metacom ? (
							<MetacomSiteSelector
								onSelect={(selection) => {
									if ("id" in selection) {
										props.edit.changeProperty("main", {
											name: selection.name,
											bstn: selection.id,
											costbase: selection.id,
										});
									} else {
										let data = { name: selection.name };

										if ("bstn" in selection && selection.bstn !== "")
											data["bstn"] = selection.bstn;

										if ("costbase" in selection && selection.costbase !== "")
											data["costbase"] = selection.costbase;

										if ("client" in selection && selection.client !== "")
											data["client"] = selection.client;

										if (
											"location" in selection &&
											"lat" in selection.location &&
											selection.location.lat !== 0
										)
											data["position"] = {
												lat: selection.location.lat,
												lng: selection.location.lng,
												zoom: 10,
											};

										props.edit.changeProperty("main", data);
									}
									props.edit.saveHistory();
								}}
							/>
						) : null}

						<BlurChangeInput
							fluid
							label={t("edit.fields.name.label")}
							placeholder={t("edit.fields.name.placeholder")}
							value={props.edit.state.data.name || ""}
							onBlur={(_) => props.edit.saveHistory()}
							disabled={!props.edit.state.data.editable}
							onChange={(e) =>
								props.edit.changeProperty("main", {
									name: e.target.value,
								})
							}
						/>
						<Form.Group widths="equal">
							<BlurChangeInput
								fluid
								label={t("edit.fields.constructionNumber.label")}
								placeholder={t("edit.fields.constructionNumber.placeholder")}
								value={props.edit.state.data.bstn || ""}
								onBlur={(_) =>{ 
									props.edit.validateBstn();
									props.edit.saveHistory();
								}}
								disabled={!props.edit.state.data.editable}
								
								onChange={(e) =>
									props.edit.changeProperty("main", {
										bstn: e.target.value,
									})
				
								}
							/>
							<BlurChangeInput
								fluid
								label={t("edit.fields.costCentre.label")}
								placeholder={t("edit.fields.costCentre.placeholder")}
								value={props.edit.state.data.costbase || ""}
								onBlur={(_) => props.edit.saveHistory()}
								disabled={!props.edit.state.data.editable}
								onChange={(e) =>
									props.edit.changeProperty("main", {
										costbase: e.target.value,
									})
								}
							/>
							<BlurChangeInput
								fluid
								label={t("edit.fields.client.label")}
								placeholder={t("edit.fields.client.placeholder")}
								value={props.edit.state.data.client || ""}
								onBlur={(_) => props.edit.saveHistory()}
								disabled={!props.edit.state.data.editable}
								onChange={(e) =>
									props.edit.changeProperty("main", {
										client: e.target.value,
									})
								}
							/>
						</Form.Group>
					</React.Fragment>
				) : (
					<React.Fragment>
						{!props.edit.isFullPlanning && props.data.state.formData.date !== false ? (
							<Form.Field>
								<label>{t("date")}</label>
								<SingleDatePicker
									date={moment(props.data.state.formData.date)} // momentPropTypes.momentObj or null,
									id="xdpicker_date" // PropTypes.string.isRequired,
									numberOfMonths={3}
									disabled={props.edit.state.data.fixed}
									isOutsideRange={(x) => props.edit.isDayFree(x)}
									onDateChange={(date) =>
										props.data.changeDate(date)
									} // PropTypes.func.isRequired,
									focused={focused} // PropTypes.bool
									onFocusChange={({ focused }) => setFocused(focused)}
									hideKeyboardShortcutsPanel
								/>
							</Form.Field>
						) : null}
						<Form.Group widths="equal">
							<Form.Field>
								<label>Beginn</label>
								<TimeInput
									name="time"
									animation="none"
									placeholder={
										props.data.state.formData.start === false
											? t("choose.same")
											: t("choose.initial")
									}
									value={
										props.data.state.formData.start === false
											? ""
											: props.data.state.formData.start
									}
									closable
									iconPosition="left"
									onChange={(e, { value }) =>
										props.data.changeTime("start", value)
									}
									onFocus={busyRanges ? () => setActiveField("start") : undefined}
									onClick={busyRanges ? () => setActiveField("start") : undefined}
									disabled={
										!props.edit.state.data.editable ||
										(!!props.data.state.formData.bpo.length && !(companyName === 'cajot'))
									}
								/>
							</Form.Field>
							{
								companyName === 'cajot' ? (
									<Form.Field>
										<label>{t("pavingStart")}</label>
										<TimeInput
											name="time"
											animation="none"
											closable
											placeholder={
												props.data.state.formData.paving_start === false
													? t("choose.same")
													: t("choose.initial")
											}
											value={
												props.data.state.formData.paving_start === false
													? ""
													: props.data.state.formData.paving_start
											}
											iconPosition="left"
											onChange={(e, { value }) =>
												props.data.changeTime("paving_start", value)
											}
											disabled={
												true
											}
										/>
									</Form.Field>
								) : null
							}
							
							<Form.Field>
								<label>Ende</label>
								<TimeInput
									name="time"
									animation="none"
									closable
									placeholder={
										props.data.state.formData.end === false
											? t("choose.same")
											: t("choose.initial")
									}
									value={
										props.data.state.formData.end === false
											? ""
											: props.data.state.formData.end
									}
									iconPosition="left"
									onChange={(e, { value }) =>
										props.data.changeTime("end", value)
									}
									onFocus={busyRanges ? () => setActiveField("end") : undefined}
									OnClick={busyRanges ? () => setActiveField("end") : undefined}
									disabled={
										!props.edit.state.data.editable ||
										(!!props.data.state.formData.bpo.length && !(companyName === 'cajot'))
									}
								/>
							</Form.Field>
						</Form.Group>
					</React.Fragment>
				)}
				{!props.global ? (
					<BlurChangeInput
						textarea={true}
						label={t("edit.fields.comments.label")}
						value={
							props.data.state.formData.comment === false
								? ""
								: props.data.state.formData.comment
						}
						onBlur={(_) => props.edit.saveHistory()}
						onChange={(e) => props.data.changeValue("comment", e.target.value)}
						disabled={!props.edit.state.data.editable}
						placeholder={
							props.data.state.formData.comment === false
								? t("edit.fields.comments.same")
								: t("edit.fields.comments.placeholder")
						}
					/>
				) : (
					<BlurChangeInput
						textarea={true}
						label={t("edit.fields.globalcomments.label")}
						disabled={!props.edit.state.data.editable}
						value={
							props.edit.state.data.comment === false
								? ""
								: props.edit.state.data.comment
						}
						onBlur={(_) => props.edit.saveHistory()}
						onChange={(e) =>
							props.edit.changeProperty("main", {
								comment: e.target.value,
							})
						}
						placeholder={t("edit.fields.globalcomments.placeholder")}
					/>
				)}
			</Form>
		</Tab.Pane>
	);
};

export default EditBasedata;
